
import { getNextDayStops } from '@/api';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';

import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import 'leaflet.markercluster';
import { NextDayStopsParams, Package } from '@/models/location';
import L, { Icon } from 'leaflet';
import { GIcon } from './Lglyph';

import BackButton from '@/components/UI/backButton/BackButton.vue';
import DatePicker from '@/views/Location/DatePicker.vue';

import { addBusinessDays, format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import _isEqual from 'lodash/isEqual';
import * as routeNames from '@/router/routeNames';
import { timeZoneDefault } from '@/utils/PortalConstants';

import { User } from '@/models/main';
import StateSelect from '@/components/searchPage/components/StateSelect/StateSelect.vue';

type D = Icon.Default & {
  _getIconUrl?: string;
};

@Component({
  components: {
    StateSelect,
    BackButton,
    DatePicker,
    LMap,
    LTileLayer,
    LMarker,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class NextDayMap extends Vue {
  private user!: User;

  public mapRef: HTMLFormElement;
  public mapAus;

  public loadingMap = false;
  public loadingDrivers = false;

  public date = '';
  public states: string[] = [];

  // Geo center of Australia
  public center = L.latLng(-23.116667, 132.133333);
  public url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  public attribution =
    '<span class="ml-1">Map data © <a href="http://openstreetmap.org" class="mx-0.5">OpenStreetMap</a> contributors</span>';
  public mapIsHovered = false;
  public ctrlBtnIsDown = false;

  public mouseover(): void {
    this.mapIsHovered = true;
    document.addEventListener('keydown', this.handleCtrl);
    document.addEventListener('keyup', this.handleCtrlUp);
  }

  public mouseleave(): void {
    this.mapIsHovered = false;
    document.removeEventListener('keydown', this.handleCtrl);
    document.removeEventListener('keyup', this.handleCtrlUp);
  }

  public handleCtrl(value): void {
    if (this.mapIsHovered && value.keyCode === 17) {
      this.mapAus.scrollWheelZoom.enable();
      this.ctrlBtnIsDown = true;
    }
  }

  public handleCtrlUp(): void {
    this.mapAus.scrollWheelZoom.disable();
    this.ctrlBtnIsDown = false;
  }

  public clickSearch(): void {
    const newRoute = {
      date: this.date,
      state: this.states.join(','),
    };
    const isEqual = _isEqual(newRoute, this.$route.query);
    if (!isEqual) {
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, ...newRoute },
      });
    } else {
      this.drawMapData(this.mapAus);
    }
  }

  public async changeDate(value: string): Promise<void> {
    this.date = value;
    this.mapAus.flyTo(this.center, 5);
  }

  private changeStates(states: string[]) {
    this.states = states;
  }

  public async initMapOnReady(): Promise<void> {
    delete (Icon.Default.prototype as D)._getIconUrl;
    Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
    this.mapRef = this.$refs.myMap as HTMLFormElement;
    this.mapAus = this.mapRef.mapObject;
    this.mapAus.scrollWheelZoom.disable();
    // Flag replacement:
    this.mapAus.attributionControl.setPrefix(
      `<span class="mt-1 mr-1">
        <?xml version="1.0" encoding="UTF-8"?>
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20px" height="10px" viewBox="0 0 20 10" version="1.1">
          <g id="surface1">
          <path style=" stroke:none;fill-rule:nonzero;fill:rgb(69.411765%,12.941176%,17.254902%);fill-opacity:1;" d="M 19.960938 0.0976562 L 15.671875 0.0976562 L 10.628906 5.003906 L 15.671875 9.910156 L 19.960938 9.910156 L 14.917969 5.003906 L 19.960938 0.0976562 "/>
          <path style=" stroke:none;fill-rule:nonzero;fill:rgb(9.411765%,13.333333%,25.882353%);fill-opacity:1;" d="M 13.664062 0.0820312 L 9.375 0.0820312 L 3.5 5.578125 L 3.5 0.0820312 L 0.113281 0.0820312 L 0.113281 9.921875 L 3.5 9.921875 L 6.457031 7.058594 L 9.375 9.898438 L 13.664062 9.898438 L 8.621094 4.992188 L 13.664062 0.0820312 "/>
          </g>
        </svg>
      </span>`,
    );
  }

  private clearMapLayers(map): void {
    // Removing all layers except the map itself
    map.eachLayer(layer => {
      if (!layer['_tiles']) {
        layer.remove();
      }
    });
  }

  private drawPackages(map, response: Package[]) {
    const pickupGlyphIcon = GIcon.glyph({
      prefix: 'mdi',
      glyph: 'mdi-alpha-d-circle',
      glyphSize: '16px',
      glyphColor: 'white',
    });
    const deliveryGlyphIcon = GIcon.glyph({
      prefix: 'mdi',
      glyph: 'mdi-alpha-p-circle',
      glyphSize: '16px',
      glyphColor: 'white',
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const markerG = L.markerClusterGroup({
      maxClusterRadius: 120,
      spiderfyOnMaxZoom: true,
    });
    const driverPackages = response
      .filter((pack: Package) => pack.point && pack.point.length > 0)
      .map((pack: Package) => {
        const marker = L.marker([Number(pack.point[0]), Number(pack.point[1])], {
          icon: pack.stop_type === 'P' ? pickupGlyphIcon : deliveryGlyphIcon,
          draggable: false,
        });
        const toRender = `Job: ${pack.rockhopper_id}; Stop Number: ${pack.stop_number};
        <br />
        Stop name: ${pack.name};
        <br />
        Stop addres: ${pack.address_line_2}, ${pack.address_line_1}, ${pack.post_code}, ${pack.suburb}, ${pack.state}
        `;
        marker.bindPopup(toRender);
        return marker;
      });

    markerG.addLayers(driverPackages);
    map.addLayer(markerG);
  }

  public async drawMapData(map) {
    this.loadingMap = true;
    const params: NextDayStopsParams = {
      state: this.states,
      date: this.date,
    };
    this.clearMapLayers(this.mapAus);
    // Draw driver locations
    const response = await getNextDayStops(params);
    // Draw packages
    this.drawPackages(map, response);
    this.loadingMap = false;
  }

  @Watch('$route.query', { immediate: true })
  private async routeChanged(): Promise<void> {
    if (this.$route.name === routeNames.NEXT_DAY_STOPS) {
      // Date: get from the query if valid or set today
      if (this.$route.query?.date && isValid(new Date(String(this.$route.query.date)))) {
        this.date = String(this.$route.query.date);
      } else {
        const userTimeZone = this.user.timezone || timeZoneDefault;
        this.date = format(utcToZonedTime(addBusinessDays(new Date(), 1), userTimeZone), 'yyyy-MM-dd');
      }
    }

    // States:
    // TODO: Need to pass this.states to the StateSelect component
    if (this.$route.query?.state) {
      const states = this.$route.query.state as string;
      if (states.toLowerCase() === 'all') {
        console.log('all');
      } else {
        this.states = states.split(',');
        console.log('states', this.states);
      }
    }
    // Wait for the DOM to update
    this.$nextTick(() => {
      this.initMapOnReady();
      if (!!this.$route.query?.date && !!this.$route.query?.state) {
        this.drawMapData(this.mapAus);
      }
    });
  }
}
